
















































































































import { Component, Mixins, Prop, Ref, Watch } from "vue-property-decorator";
import WindowEvent from "@/decorators/WindowEvent";
import Broadcaster, {
  BroadcastersCatalog,
} from "@/models/SpecialInitiatives/Broadcaster";
import MarketMixin from "@/mixins/http/SpecialInitiatives/MarketMixin";
import EntryItem from "@/components/EntryItem.vue";

type Kind = "TV" | "RADIO";

interface SpecialInitiativesColumn {
  value: string;
  label: string;
  isHighlight?: boolean;
}

interface SpecialInitiativesRow {
  subject: string;
  broadcaster?: string;
  kind: Kind | "";
  data: { [key: string]: { value: number; quote: string } };
  children: SpecialInitiativesRow[];
  isTotal: boolean;
}
type CollectionData = {
  col: string;
  value: number;
  quote: number;
  companyIds: Set<string>;
};
type SubjectCollection = { [key: string]: CollectionData[] };
type ParentRecord = {
  data: CollectionData[];
  subjects: SubjectCollection;
  mediaType: Kind;
};
type ParentCollection = { [key: string]: ParentRecord };

export interface SpecialInitiativesIData {
  [key: string]: Broadcaster[];
}
export type SubjectColumn = "sales_agencies" | "editors" | "market_segments";
export interface SpecialInitiativesIColumn {
  name: string;
  label: string;
}

@Component({
  components: { EntryItem },
})
export default class SpecialInitiativesTable extends Mixins(MarketMixin) {
  @Prop({ required: true }) readonly weekRanges!: SpecialInitiativesIColumn[];
  @Prop({ required: true }) readonly broadcasters!: BroadcastersCatalog;
  @Prop({ required: true }) readonly subject!: SubjectColumn;
  @Prop({ default: false }) readonly loading!: boolean;
  @Prop({ required: true }) readonly grouped!: boolean;
  @Prop({ required: true }) readonly quoted!: boolean;
  @Prop({ required: true }) readonly unitOfMeasurement!: string;
  @Prop({ required: true }) readonly exclusivity!: string;

  @Ref() inner!: HTMLDivElement;
  @Ref() table!: HTMLTableElement;

  position = {
    overflow: false,
    scrolledToRight: false,
    scrolledToLeft: true,
  };

  @Watch("unitOfMeasurement", { immediate: true })
  onUnitChanged(value: string) {
    this.buildSpecialInitiativesColumnsAndRows(
      this.loading,
      this.exclusivity,
      this.subject,
      this.broadcasters,
      this.weekRanges,
      value
    );
  }

  mounted() {
    this.checkPosition();
    this.buildSpecialInitiativesColumnsAndRows(
      this.loading,
      this.exclusivity,
      this.subject,
      this.broadcasters,
      this.weekRanges,
      this.unitOfMeasurement
    );
  }

  protected getColumnClass(column: SpecialInitiativesColumn) {
    if (this.sortDirection === null) return {};
    const columnClass = {
      "breakdown-table__column--sorted": column.value === this.sortColumn,
      "breakdown-table__column--sorted--asc":
        column.value === this.sortColumn && this.sortDirection === "ASC",
      "breakdown-table__column--sorted--desc":
        column.value === this.sortColumn && this.sortDirection === "DESC",
    };
    return columnClass;
  }

  @Watch("broadcasters")
  onBroadcastersChanged() {
    this.checkPosition();
    if (this.columns.length > 1 && this.sortDirection === null) {
      this.sortColumn = this.columns[1].value;
      this.sortDirection = "DESC";
    } else {
      this.buildSpecialInitiativesColumnsAndRows(
        this.loading,
        this.exclusivity,
        this.subject,
        this.broadcasters,
        this.weekRanges,
        this.unitOfMeasurement
      );
    }
  }

  @Watch("subject")
  onSubjectChange() {
    this.buildSpecialInitiativesColumnsAndRows(
      this.loading,
      this.exclusivity,
      this.subject,
      this.broadcasters,
      this.weekRanges,
      this.unitOfMeasurement
    );
  }

  @Watch("weekRanges", { deep: true })
  onWeekRangesChange() {
    this.buildSpecialInitiativesColumnsAndRows(
      this.loading,
      this.exclusivity,
      this.subject,
      this.broadcasters,
      this.weekRanges,
      this.unitOfMeasurement
    );
  }

  @WindowEvent("resize")
  onResize() {
    if (!this.inner) {
      return;
    }
    this.checkPosition();
  }

  protected scrollToLeft() {
    this.inner.scrollLeft = 0;
    this.checkPosition();
  }

  protected scrollToRight() {
    this.inner.scrollLeft = this.getTableWidth() - this.getElementWidth();
    this.checkPosition();
  }

  protected checkPosition() {
    this.position.overflow = this.getElementWidth() < this.getTableWidth();
    this.position.scrolledToRight =
      this.inner.scrollLeft == this.getTableWidth() - this.getElementWidth();
    this.position.scrolledToLeft = this.inner.scrollLeft == 0;
  }

  protected getElementWidth(): number {
    return this.inner?.clientWidth || 0;
  }

  protected getTableWidth(): number {
    return this.table?.clientWidth || 0;
  }
}
